/**
 * Casinetto Cart Sync (B2C-214)
 * ---------------------------------------------------------------
 * Cross-device cart sync via the customer metafield `custom.cart`
 * (shared with the Flutter app — contract in
 * docs/integration/cart-sync-metafield-contract.md).
 *
 * Design: docs/superpowers/specs/2026-09-02-cart-sync-service-owned-cart-design.md
 *
 * The account's cart belongs to the SERVICE and is named by `custom.cart`. This
 * browser's Shopify cookie cart is a WORKING COPY, and checkout is the only
 * thing it is authoritative for. That inversion is the whole design, and it
 * exists because the theme cannot forget a cart without deleting it: the `cart`
 * cookie is not rotatable (`cart_sig` is httpOnly — deletable, not forgeable),
 * so while the pointer named the cookie cart, releasing it at logout emptied
 * the customer's basket (D4) and not releasing it left a stale copy that
 * re-merged into itself (D4b). Owning nothing removes both.
 *
 * This module is the DOWN direction plus the identity moments. The up direction
 * is entirely cart-mirror.js.
 *
 *   reconcile()  the account's cart is the truth; make the working copy equal
 *                to it. Runs on EVERY page load for a logged-in customer, and
 *                on demand before a cart surface. It used to run on a narrower
 *                set of triggers; that narrowing caused three measured defects
 *                and was reverted — see start().
 *   release()    logout: empty the working copy, leave the account's cart alone.
 *
 * The theme no longer writes the pointer. The service does, on the first mirror
 * write, so /apps/cart-sync/pointer is unused from here.
 *
 * Exposes: window.CasinettoCartSync = { awaitSync, reconcile }
 * Events:  'cart:update' on document, detail.data.source 'cart-sync' — and
 *          'cart-sync-release' for the logout clear, which the mirror must
 *          ignore (docs/events.md). Emit-only, never listens.
 *
 * Failure semantics: every failure -> one console.warn, never throw. Nothing is
 * marked done, so the next trigger retries.
 */
(function () {
  'use strict';

  /* ---- Constants ------------------------------------------------ */

  // Customer id ONLY — never 'guest', and never written on an anonymous render.
  // Absent = this browser has never seen a logged-in customer, which is one of
  // the two signals that the working copy is an anonymous basket.
  var LS_CUSTOMER = 'casinetto_cart_customer';

  // Written at logout, next to the clear, and the OTHER half of that signal:
  // "we released the working copy ourselves, so whatever is in it now was added
  // after that". Without it, `logout -> shop -> log back in as the same
  // customer` reads as a leftover and the anonymous additions are discarded —
  // which is precisely the case this feature exists to serve.
  var LS_RELEASED = 'casinetto_cart_released';

  // Whether the pointer names a cart THIS BROWSER owns — i.e. its own cookie
  // cart. Set by every reconciliation, and the only thing that makes the logout
  // release safe.
  //
  // The design assumed no migration was needed: "the first mirror write creates
  // a service cart and repoints". That is false when nothing is ever written. X
  // logs in where the pointer already names this browser's cookie cart,
  // reconciliation adopts (the two are identical, so it is a no-op), no mutation
  // happens, so no mirror write happens, so the pointer never moves. A release
  // then empties the cart the pointer names — D4, on a pointer that was never
  // migrated. And it cannot be fixed by pushing: writing lines to an existing
  // usable pointer applies them IN PLACE and does not repoint, so the pointer
  // would stay ours forever and the release stay unsafe forever.
  //
  // ABSENT MEANS UNSAFE. A browser that has never reconciled, or whose
  // reconciliation failed, does not release — which also makes the release safe
  // when the endpoint is not deployed at all, since no mirror write can then
  // move the pointer off our own cart.
  var LS_POINTER_FOREIGN = 'casinetto_cart_pointer_foreign';

  var DELIVERY_SS = 'casinetto_cart_synced';        // delivery-engine's flag
  var TIMEOUT_MS  = 5000;                           // per request
  var AWAIT_MS    = 2000;                           // checkout race timeout

  /* ---- Internal state ------------------------------------------- */

  var _script  = document.currentScript || document.querySelector('script[data-cart-sync]');
  var _customerId = '';
  if (_script) { _customerId = (_script.getAttribute('data-customer-id') || '').trim(); }

  var _pending = null;
  var _replayArmed = false;

  // When the last reconciliation finished, and how recently counts as current.
  //
  // The cart surfaces call reconcile() on demand because the read side used to
  // run on a narrow set of triggers. It now runs on every page load, so a
  // drawer opened right after a reload was starting a SECOND full round trip
  // and re-rendering everything a few seconds later — the shopper sees the cart
  // settle, then move again. Matteo observed exactly that.
  //
  // The on-demand call is still right when the page has been open a while: the
  // app can change the cart at any time. So it is a freshness window, not a
  // removal. The number is a judgement, not a derivation — long enough to cover
  // "reload, then open the drawer", short enough that a deliberate look after
  // browsing gets current data.
  var _lastReconciled = 0;
  var FRESH_MS = 15000;

  /* ---- Helpers --------------------------------------------------- */

  function warn(message, err) {
    if (err) { console.warn('[cart-sync] ' + message, err); return; }
    console.warn('[cart-sync] ' + message);
  }

  function timedFetch(url, options) {
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, TIMEOUT_MS);
    var config = options || {};
    config.signal = controller.signal;
    return fetch(url, config).finally(function () { clearTimeout(timer); });
  }

  function postJson(url, body) {
    return timedFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });
  }

  function ssRemove(key) {
    try { sessionStorage.removeItem(key); } catch (_) { /* */ }
  }

  function lsGet(key) {
    try { return localStorage.getItem(key); } catch (_) { return null; }
  }
  function lsSet(key, value) {
    try { localStorage.setItem(key, value); } catch (_) { /* */ }
  }
  function lsRemove(key) {
    try { localStorage.removeItem(key); } catch (_) { /* */ }
  }

  function dispatchCartUpdate(source) {
    document.dispatchEvent(new CustomEvent('cart:update', {
      detail: { data: { source: source || 'cart-sync' } }
    }));
  }

  function fireCartUpdate(source) {
    dispatchCartUpdate(source);
    // Every consumer of this event (header badge, cart drawer, pcard steppers)
    // registers inside a section's {% javascript %} bundle, which Shopify loads
    // on its own schedule — measured LATER than this module, which runs at
    // DOMContentLoaded from a deferred script. A reconciliation emits late
    // enough to win that race because a proxy round trip precedes it; the
    // logout release does not, and its event was received by nobody (measured
    // 1 Sep: the badge kept the previous count until the next navigation). An
    // event nobody can hear is not an event, so replay it once when the page is
    // complete. Safe by construction: every consumer re-reads /cart.js and is
    // idempotent, so a duplicate is a no-op. Normative in docs/events.md —
    // copy this shape, do not invent a timer.
    if (document.readyState === 'complete' || _replayArmed) { return; }
    _replayArmed = true;
    window.addEventListener('load', function () { dispatchCartUpdate(source); }, { once: true });
  }

  function mirror() {
    return window.CasinettoCartMirror || null;
  }

  // Token-only, and the key is irrelevant: two values naming the same cart may
  // carry it differently, and the question here is only "is this the same cart".
  function cartToken(gidOrToken) {
    if (!gidOrToken) { return ''; }
    var value = String(gidOrToken);
    var slash = value.lastIndexOf('/');
    if (slash !== -1) { value = value.slice(slash + 1); }
    var query = value.indexOf('?');
    if (query !== -1) { value = value.slice(0, query); }
    return value;
  }

  /* ---- Backend (app proxy) --------------------------------------- */

  function resolveRemote() {
    return timedFetch('/apps/cart-sync/resolve', { headers: { 'Accept': 'application/json' } })
      .then(function (response) {
        if (!response.ok) { throw new Error('resolve ' + response.status); }
        return response.json();
      });
  }

  /* ---- Line shaping ---------------------------------------------- */

  function attributesToProperties(attrs) {
    if (!attrs || !attrs.length) { return null; }
    var properties = {};
    attrs.forEach(function (attr) {
      if (attr && attr.key != null) {
        properties[attr.key] = attr.value == null ? '' : attr.value;
      }
    });
    return properties;
  }

  // One entry per LOGICAL line, which is not one per physical line: analytics
  // attributes are excluded from identity, so the app's line and the web's line
  // for one product share a key. Each entry therefore carries the total
  // quantity, the physical line to write to, the extra physical lines to remove,
  // and the fingerprint of the primary line's complete attribute set so the
  // branches below can tell "same product" from "same attributes".
  function localByKey(cart) {
    var byKey = {};
    var items = (cart && cart.items) || [];
    items.forEach(function (item) {
      var key = window.CasinettoCart.buildLineKey(item.variant_id, item.properties);
      var entry = byKey[key];
      if (!entry) {
        byKey[key] = {
          lineKey: item.key,
          qty: item.quantity,
          fingerprint: window.CasinettoCart.attributesFingerprint(item.properties),
          extras: []
        };
        return;
      }
      // A duplicate of a line already seen. Fold its quantity in and mark the
      // physical line for removal: the product rule is one line per product.
      entry.qty += item.quantity;
      entry.extras.push(item.key);
    });
    return byKey;
  }

  // `/cart/change.js` leaves properties alone when the field is absent, so an
  // empty object is how a line's attributes get CLEARED.
  function propertiesFor(line) {
    return attributesToProperties(line.attributes) || {};
  }

  function addItemFor(line) {
    var addItem = {
      id: window.CasinettoCart.variantId(line.merchandiseId),
      quantity: line.quantity
    };
    var properties = attributesToProperties(line.attributes);
    if (properties) { addItem.properties = properties; }
    return addItem;
  }

  /* ---- Writing to the working copy ------------------------------- */

  // One primitive for both branches. `changes` are {id, quantity} for
  // /cart/change.js (quantity 0 removes); `adds` are /cart/add.js items.
  // Strictly sequential — Shopify's cart API rejects concurrent writes — and
  // changes first, because they address snapshot line keys that an add can
  // invalidate.
  function applyToWorkingCopy(changes, adds) {
    var skipped = 0;
    var chain = Promise.resolve();

    changes.forEach(function (change) {
      chain = chain.then(function () {
        // Only HTTP errors (line vanished in another tab -> 404) count as
        // skipped. A network rejection propagates and fails the whole
        // reconciliation: nothing is marked done and the next trigger retries.
        return postJson('/cart/change.js', change).then(function (response) {
          if (!response.ok) { skipped += 1; }
        });
      });
    });

    chain = chain.then(function () {
      if (!adds.length) { return null; }
      return postJson('/cart/add.js', { items: adds }).then(function (response) {
        if (response.ok) { return null; }
        // Shopify 422s the whole batch when any item is unaddable — fall back
        // to per-line adds, skipping failures without aborting.
        var perLine = Promise.resolve();
        adds.forEach(function (addItem) {
          perLine = perLine.then(function () {
            return postJson('/cart/add.js', { items: [addItem] })
              .then(function (lineResponse) {
                if (!lineResponse.ok) { skipped += 1; }
              });
          });
        });
        return perLine;
      });
    });

    return chain.then(function () {
      if (skipped > 0) { warn('reconciliation skipped ' + skipped + ' unaddable line(s)'); }
    });
  }

  /* ---- The two branches ------------------------------------------ */

  // ADOPT. The account's cart is the truth: the working copy is made EQUAL to
  // it — quantities changed, missing lines added, extra lines removed. One
  // rule, no conflict table, no merge base. This is what makes a leftover
  // working copy harmless instead of something that has to be told apart from
  // a real basket: it is simply overwritten.
  function adoptInto(remote, cart) {
    var local = localByKey(cart);
    var seen = {};
    var changes = [];
    var adds = [];

    (remote.lines || []).forEach(function (line) {
      var key = window.CasinettoCart.buildLineKey(line.merchandiseId, line.attributes);
      seen[key] = true;
      var localLine = local[key];
      if (!localLine) { adds.push(addItemFor(line)); return; }

      // "Equal to the account's cart" has to include the ATTRIBUTES, not only
      // the quantity. Same product, different attribute sets — the app writes
      // analytics keys and the web does not — is the ordinary case now that
      // identity ignores those keys. Leaving the local set alone would make the
      // mirror send an attribute set the service does not recognise, and the
      // service would add a line: a duplicate generated by the reconciliation
      // that exists to prevent one.
      var remoteFingerprint = window.CasinettoCart.attributesFingerprint(line.attributes);
      if (localLine.fingerprint !== remoteFingerprint) {
        changes.push({
          id: localLine.lineKey,
          quantity: line.quantity,
          properties: propertiesFor(line)
        });
      } else if (localLine.qty !== line.quantity) {
        changes.push({ id: localLine.lineKey, quantity: line.quantity });
      }

      // Fold away any duplicate physical lines for this product.
      localLine.extras.forEach(function (extra) {
        changes.push({ id: extra, quantity: 0 });
      });
    });

    Object.keys(local).forEach(function (key) {
      if (seen[key]) { return; }
      // Present locally, absent from the account's cart -> remove, every
      // physical line of it. Sound only because this branch never runs against
      // an EMPTY remote (see reconcile).
      changes.push({ id: local[key].lineKey, quantity: 0 });
      local[key].extras.forEach(function (extra) {
        changes.push({ id: extra, quantity: 0 });
      });
    });

    return applyToWorkingCopy(changes, adds);
  }

  // SUM. The one case where the working copy is not overwritten: it holds an
  // anonymous basket, and contract §2 says shared lines sum. Reachable only
  // when this browser has never seen a customer, or when we released the
  // working copy at logout ourselves.
  function sumInto(remote, cart) {
    var local = localByKey(cart);
    var changes = [];
    var adds = [];

    (remote.lines || []).forEach(function (line) {
      var key = window.CasinettoCart.buildLineKey(line.merchandiseId, line.attributes);
      var localLine = local[key];
      if (!localLine) { adds.push(addItemFor(line)); return; }
      // Sum, and adopt the account's attributes on the surviving line for the
      // same reason adoptInto does: what the mirror sends next has to be an
      // attribute set the service recognises.
      var change = { id: localLine.lineKey, quantity: localLine.qty + line.quantity };
      var remoteFingerprint = window.CasinettoCart.attributesFingerprint(line.attributes);
      if (localLine.fingerprint !== remoteFingerprint) {
        change.properties = propertiesFor(line);
      }
      changes.push(change);
      localLine.extras.forEach(function (extra) {
        changes.push({ id: extra, quantity: 0 });
      });
    });

    return applyToWorkingCopy(changes, adds);
  }

  /* ---- Reconciliation -------------------------------------------- */

  // The working copy holds an anonymous basket when either signal says so.
  // Both are needed: `logout -> shop -> log back in as the same customer`
  // leaves the identity key unchanged, so without the release flag it would
  // read as a leftover and the anonymous additions would be adopted away.
  function workingCopyIsAnonymous() {
    if (!lsGet(LS_CUSTOMER)) { return true; }
    if (lsGet(LS_RELEASED)) { return true; }
    return false;
  }

  function pushWorkingCopy(m) {
    if (m && m.pushAll) { return m.pushAll(); }
    warn('mirror unavailable, working copy not pushed');
    return Promise.resolve();
  }

  function runReconcile() {
    if (!_customerId) { return Promise.resolve(); }
    if (!window.CasinettoCart || !window.CasinettoCart.fetchCart) {
      warn('CasinettoCart unavailable, reconciliation skipped');
      return Promise.resolve();
    }

    var m = mirror();

    // Flush FIRST, and ABORT if it did not drain. Reconciling with unsent
    // intents in the queue means undoing them: the account's cart does not know
    // about them yet, so adopting reverts the shopper's own change.
    //
    // Ordering alone was not enough, and the previous version of this comment
    // claimed a guarantee the code did not implement. flush() always RESOLVES —
    // on a retryable failure it schedules a backoff retry and returns — so the
    // reconciliation proceeded regardless and adopted over the unsent work.
    //
    // Today that is survivable by accident: the queued intent fires later and
    // applies, so the change lands via the overwrite path this design exists to
    // remove. Once the service enforces `expectedQuantity` (FT-833) it is not:
    // the adopt has moved the base, the intent is rejected as stale, and the
    // rejection handler drops it. The shopper's change would be gone.
    //
    // The queue drains either way — retryable failures through the backoff,
    // non-retryable ones by being dropped — so bailing cannot stall for good.
    // The next trigger reconciles.
    var flushed = (m && m.flush) ? m.flush() : Promise.resolve();

    return flushed
      .then(function () {
        var stillQueued = (m && m.pending) ? m.pending() : 0;
        if (stillQueued) {
          warn(stillQueued + ' intent(s) still unsent — reconciliation deferred');
          throw new Error('queue not drained');
        }
        return resolveRemote();
      })
      .then(function (remote) {
        return window.CasinettoCart.fetchCart().then(function (cart) {
          // EMPTY shape (contract §3): no pointer, or the cart it named is dead
          // or completed. NEVER adopt this. Adopting an empty remote empties
          // the working copy because the POINTER died — losing a basket to a
          // dead reference. It is the same mistake, in the other direction, as
          // conflating "I could not ask" with "there is nothing there" (the
          // spec's failed-read section). Push instead: the service creates a
          // cart from what we have and repoints.
          if (!remote || !remote.cartId) {
            // No pointer to judge. Leave the flag alone rather than guessing:
            // its absence already means "do not release", which is correct
            // here — the service is about to create a cart and repoint, and
            // until the next reconciliation confirms that, this browser cannot
            // know the pointer stopped being its own.
            return pushWorkingCopy(m);
          }

          // The one thing that makes the logout release safe. Recorded before
          // either branch runs, because both mutate the working copy and its
          // token is what the comparison rests on.
          if (cartToken(remote.cartId) === cartToken(cart.token)) {
            lsRemove(LS_POINTER_FOREIGN);
          } else {
            lsSet(LS_POINTER_FOREIGN, '1');
          }

          if (workingCopyIsAnonymous()) {
            return sumInto(remote, cart).then(function () {
              // The WHOLE working copy goes up, not the diff: the mirror's
              // snapshot was seeded pre-merge, so a diff would name only the
              // lines the merge added — the account's own, hence no-ops — and
              // the anonymous lines, the entire point of the merge, would never
              // reach the account.
              return pushWorkingCopy(m);
            });
          }

          return adoptInto(remote, cart).then(function () {
            // Now equal to the account's cart, so the mirror must not report a
            // difference. Re-read rather than reusing `cart`: adoptInto just
            // wrote to it.
            return window.CasinettoCart.fetchCart().then(function (fresh) {
              if (m && m.adopt) { m.adopt(fresh); }
            });
          });
        });
      })
      .then(function () {
        lsRemove(LS_RELEASED);
        lsSet(LS_CUSTOMER, _customerId);
        _lastReconciled = Date.now();
        fireCartUpdate();
      })
      .catch(function (err) {
        // Nothing marked done, nothing stored: the next trigger retries. In
        // particular LS_RELEASED survives a failure, so a failed reconciliation
        // cannot turn an anonymous basket into a leftover.
        warn('reconciliation failed, will retry on the next trigger', err);
      });
  }

  /* ---- Logout ---------------------------------------------------- */

  // Logout releases the working copy without deleting the account's cart —
  // possible only because the pointer names a cart this browser does not own.
  // Under the old design this exact clear emptied the pointed cart, and that
  // was D4's data loss.
  function release() {
    // Fail closed. Without a reconciliation having confirmed the pointer names
    // someone else's cart, clearing the working copy may be clearing the
    // account's basket. Doing nothing here is exactly the pre-release
    // behaviour: the working copy survives logout, and the next
    // reconciliation adopts it away.
    if (!lsGet(LS_POINTER_FOREIGN)) {
      warn('logout release skipped — the pointer still names this browser\'s own cart');
      return Promise.resolve();
    }

    var m = mirror();
    // Suppress BEFORE clearing. The clear emits cart:update like any other
    // change, and a mirror that heard it would push "every line to quantity 0"
    // — emptying the account's cart, i.e. D4 wearing a new coat. The mirror
    // also ignores this event's source; neither guard is meant to be the only
    // one.
    if (m && m.suppress) { m.suppress(); }

    return postJson('/cart/clear.js', {})
      .then(function (response) {
        if (!response.ok) { throw new Error('clear.js ' + response.status); }
        lsSet(LS_RELEASED, '1');
        // Cross-module: /cart/clear.js also wiped the Region/Area cart
        // attributes delivery-engine wrote this session. Removing its
        // once-per-session flag makes it re-write them on the next page load
        // through its existing init path.
        ssRemove(DELIVERY_SS);
        // Re-derived by the next reconciliation. Left in place it would claim
        // something about a pointer this browser has not looked at since.
        lsRemove(LS_POINTER_FOREIGN);
        fireCartUpdate('cart-sync-release');
      })
      .catch(function (err) {
        // LS_RELEASED deliberately NOT written: the working copy still holds
        // the customer's lines, so claiming we released it would make the next
        // login SUM a leftover into the account's cart. Failing closed here
        // costs a stale working copy, which the next reconciliation adopts
        // away.
        warn('logout release failed', err);
      });
  }

  function onDocumentClick(event) {
    var target = event.target;
    if (!target || !target.closest) { return; }
    // One delegated listener covers all three logout links — header dropdown,
    // header account sheet, account sidebar — and any added later, without
    // touching those surfaces.
    //
    // Matched on the path ENDING in /logout rather than on a named route. The
    // first version of this listener looked for `/account/logout` and silently
    // did nothing: `routes.account_logout_url` renders
    // `/customer_identity/logout` here, because the store is on New Customer
    // Accounts (measured 2026-09-02 — no error, no clear, the working copy
    // simply surviving logout as if the feature did not exist).
    //
    // Route-agnostic is not extra work for a second flow, it is LESS work than
    // naming one: Shopify owns this route and has already changed it once.
    //
    // Ends-with rather than contains: the cost of a false positive is clearing
    // the shopper's working copy on a link that merely has the word in it.
    var link = target.closest('a[href*="logout"]');
    if (!link) { return; }
    var path = '';
    try {
      path = new URL(link.href, window.location.origin).pathname;
    } catch (_) {
      path = link.getAttribute('href') || '';
    }
    if (!/\/logout\/?$/.test(path)) { return; }
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) { return; }

    event.preventDefault();
    var href = link.href;
    var go = function () { window.location.href = href; };
    // The navigation happens even if the clear hangs: a shopper who clicked
    // sign out has to end up signed out. The release is best-effort, and a
    // missed one degrades to a stale working copy rather than a stuck page.
    Promise.race([
      release(),
      new Promise(function (resolve) { setTimeout(resolve, TIMEOUT_MS); })
    ]).then(go, go);
  }

  /* ---- Public API ------------------------------------------------ */

  window.CasinettoCartSync = {
    /** Make sure a reconciliation has run, then resolve — bounded by AWAIT_MS.
     *  Call before navigating to checkout. Always resolves, never rejects.
     *
     *  This TRIGGERS as well as awaits, which it did not have to before: the
     *  read side no longer runs on every page load, so a checkout click can
     *  arrive with nothing in flight and a working copy that has never been
     *  reconciled this session. Awaiting `_pending` alone would resolve
     *  instantly and check out a stale basket. The AWAIT_MS race is why this
     *  stays the checkout gate rather than a bare reconcile(): checkout must
     *  never hang on the proxy. */
    awaitSync: function () {
      var work = _pending;
      if (!work) {
        work = runReconcile().then(function () { _pending = null; });
        _pending = work;
      }
      return Promise.race([
        work,
        new Promise(function (resolve) { setTimeout(resolve, AWAIT_MS); })
      ]);
    },

    /** Reconcile now. Called by the surfaces that depend on the cart being
     *  right — the drawer on open, the cart page on load — because the read
     *  side deliberately does not run on every page load. Coalesces with an
     *  in-flight run. Always resolves. */
    reconcile: function () {
      if (_pending) { return _pending; }
      // Already current: the page-load reconciliation covers this. Skipping is
      // what stops a drawer opened straight after a reload from re-rendering
      // the cart a second time under the shopper.
      if (_lastReconciled && (Date.now() - _lastReconciled) < FRESH_MS) {
        return Promise.resolve();
      }
      _pending = runReconcile().then(function () { _pending = null; });
      return _pending;
    }
  };

  /* ---- Auto-init -------------------------------------------------- */

  function start() {
    // Registered for every visitor: the listener has to exist on the page where
    // the shopper clicks sign out, and that page is rendered logged-in.
    document.addEventListener('click', onDocumentClick);

    if (!_customerId) { return; }

    // EVERY page load, for a logged-in customer. This replaced a narrower set
    // of triggers — identity change, plus once per browser session — and the
    // narrowing was not a cost saving, it was the cause of three defects
    // measured on 2026-09-02:
    //
    //   - the cart drawer opened on the pre-reconciliation cart and shifted
    //     under the shopper seconds later, because it had to reconcile on open;
    //   - a product card rendered "Add to cart" for a line the account's cart
    //     already held;
    //   - and, not cosmetic: an add computed from that stale working copy sent
    //     an ABSOLUTE quantity, so a product added on two devices ended at one.
    //     A set operation carries no evidence of the base it came from.
    //
    // The premise for narrowing was that the stated freshness requirement is
    // one-directional, web -> app, with none for app -> web. True of DISPLAY
    // and false of CORRECTNESS: a working copy that has not seen the other
    // device's change does not merely show a stale number, it writes one.
    //
    // Cost, accepted knowingly by Matteo: one signed GET per page load. In the
    // steady state the adopt that follows is a no-op, so it is a read and
    // nothing else — no cart writes. And it drains the mirror queue on every
    // page load, which the narrow version did not.
    _pending = runReconcile().then(function () { _pending = null; });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
